import moment from 'moment';

import {
  isTemplatePlaceholder,
  QFilter,
  SupporterStatus,
  TASK_KIND_MAPPING_REVERSE,
  TaskChoice,
  TaskKind,
} from '@/types';
import { formatTemplateCapableDateForFilterValue } from '@/util';
import { FilterCriteria } from '@/filterCriteria/types';

export const standardDateFormat = 'YYYY-MM-DD';

/**
 * Combine the various inputs into a consistent QFilter. This involves things
 * like splitting contactableCriteria out into separate bits, and not including
 * criteria if their underlying input is empty-like.
 *
 * @param   data  - The different states of the inputs from the quick search page
 *
 * @returns The data rejiggered into a QFilter
 */
export function buildQFilter({
  geometries,
  groups,
  tags,
  contactable,
  lists,
  statuses,
  eventTypes,
  eventDateRange,
  events,
  eventCampaigns,
  election,
  electorates,
  rostertypes,
  rostercriteria,
  memberships,
  branches,
  notRenewing,
  supporterstatuses,
  taskelections,
  issincespecificelection,
  taskchoices,
  taskkinds,
  tasknote,
  mailings,
  mailingstatus,
  activities,
  activityStatuses,
  activityDateRange,
  subject,
  age,
  pathwaySteps,
  callingstatuses,
  callingstatusinclusion,
  callingstatusDateRange,
  callingstatusLists,
  callingstatusUsers,
  callingstatusCampaigns,
  commspreferences,
  commspreferencesinclusion,
  locationOperator,
}: FilterCriteria): QFilter {
  const qFilter: QFilter = {};

  if (geometries.length > 0) {
    qFilter.geometryIds = geometries.map((x) => x.id);
    qFilter.locationOperator = locationOperator;
  }
  if (groups.length > 0) qFilter.groupIds = groups.map((x) => x.id);
  if (tags.length > 0) qFilter.tagIds = tags.map((x) => x.id);
  if (pathwaySteps.length > 0) qFilter.pathwaySteps = pathwaySteps.map((step) => step.id);
  if (lists.length > 0) qFilter.listIds = lists.map((x) => x.id);
  if (statuses.length > 0) qFilter.statusIds = statuses.map((x) => x.label);
  if (eventTypes.length > 0) qFilter.eventTypeIds = eventTypes.map((x) => x.id);
  if (events.length > 0) qFilter.eventIds = events.map((x) => x.id);
  if (eventCampaigns.length > 0) qFilter.eventCampaignIds = eventCampaigns.map((x) => x.id);
  if (election) qFilter.electionId = election.id;
  if (electorates.length > 0) qFilter.electorateIds = electorates.map((x) => x.id);
  if (rostertypes.length > 0 && election) qFilter.rosterTypes = rostertypes.map((x) => x.label);
  if (
    'votingPeriod' in rostercriteria &&
    election &&
    !isTemplatePlaceholder(rostercriteria.votingPeriod)
  ) {
    qFilter.votingPeriod = rostercriteria.votingPeriod;
  }
  if (
    'shiftStatus' in rostercriteria &&
    election &&
    !isTemplatePlaceholder(rostercriteria.shiftStatus)
  ) {
    qFilter.shiftStatus = rostercriteria.shiftStatus;
  }
  if ('hasPhone' in contactable) qFilter.hasPhone = contactable.hasPhone;
  if ('hasEmail' in contactable) qFilter.hasEmail = contactable.hasEmail;
  if (eventDateRange?.startDate || eventDateRange?.endDate) {
    const eventDateRangeWithPlaceholders = {
      startDate: formatTemplateCapableDateForFilterValue(
        eventDateRange.startDate,
        isTemplatePlaceholder,
        undefined
      ),
      endDate: formatTemplateCapableDateForFilterValue(
        eventDateRange.endDate,
        isTemplatePlaceholder,
        undefined
      ),
    };
    if (isTemplatePlaceholder(eventDateRangeWithPlaceholders?.startDate)) {
      eventDateRangeWithPlaceholders.startDate = undefined;
    }
    if (isTemplatePlaceholder(eventDateRangeWithPlaceholders?.endDate)) {
      eventDateRangeWithPlaceholders.endDate = undefined;
    }
    qFilter.eventDateRange = eventDateRangeWithPlaceholders as
      | {
          startDate?: string | undefined;
          endDate?: string | undefined;
        }
      | undefined;
  }
  if (memberships.length > 0) qFilter.membershipIds = memberships.map((x) => x.id);
  if (branches && branches.length > 0) qFilter.branchIds = branches.map((x) => x.id);
  if (notRenewing) qFilter.notRenewing = notRenewing;
  if (supporterstatuses.length > 0)
    qFilter.supporterStatuses = supporterstatuses.map(
      (x) => String.fromCodePoint(x.id) as SupporterStatus
    );

  if (taskelections && taskelections.length > 0)
    qFilter.taskElectionIds = taskelections.map((x) => x.id);
  if (typeof issincespecificelection !== 'undefined' && taskelections && taskelections.length > 0)
    qFilter.isSinceSpecificElection = issincespecificelection;

  // Most task choices are represented by a single character, except for Unknown, which is blannk.

  if (taskchoices && taskchoices.length > 0)
    qFilter.taskChoices = taskchoices.map((x) =>
      x.id === 0 ? '' : (String.fromCodePoint(x.id) as TaskChoice)
    );

  if (taskkinds && taskkinds.length > 0)
    qFilter.taskKinds = taskkinds.map((x) => TASK_KIND_MAPPING_REVERSE[x.label] as TaskKind);

  if (tasknote) qFilter.taskNote = tasknote;

  if (mailings && mailings.length > 0) qFilter.mailingIds = mailings.map((x) => x.id);

  if (mailingstatus) qFilter.mailingStatus = mailingstatus;

  if (activities && activities.length > 0) {
    qFilter.activityIds = activities.map((x) => x.id);
    qFilter.activityDateRange = {
      ...activityDateRange,
      displayedStartDate:
        activityDateRange.displayedStartDate &&
        !isTemplatePlaceholder(activityDateRange.displayedStartDate)
          ? moment(activityDateRange.displayedStartDate).format(standardDateFormat)
          : null,
      displayedEndDate:
        activityDateRange.displayedEndDate &&
        !isTemplatePlaceholder(activityDateRange.displayedEndDate)
          ? moment(activityDateRange.displayedEndDate).format(standardDateFormat)
          : null,
    };
  }

  if (activityStatuses && activityStatuses.length > 0)
    qFilter.activityStatusIds = activityStatuses.map((x) => x.id);

  if (subject) qFilter.subject = subject;

  if (!!age && !!age.value && !!age.range) qFilter.age = { value: age.value, range: age.range };

  if (callingstatuses && callingstatuses.length > 0) {
    qFilter.callingStatusLabels = callingstatuses.map((x) => x.label);

    const { displayedStartDate, displayedEndDate } = callingstatusDateRange;

    qFilter.callingStatusDateRange = {
      ...callingstatusDateRange,
      displayedStartDate: displayedStartDate
        ? moment(displayedStartDate).format(standardDateFormat)
        : null,
      displayedEndDate: displayedEndDate
        ? moment(displayedEndDate).format(standardDateFormat)
        : null,
    };
  }

  if (callingstatusinclusion === true) qFilter.callingStatusInclusion = true;
  else if (callingstatusinclusion === false) qFilter.callingStatusInclusion = false;

  if (callingstatusLists.length > 0)
    qFilter.callingStatusListIds = callingstatusLists.map((x) => x.id);

  if (callingstatusUsers.length > 0)
    qFilter.callingStatusUserSubs = callingstatusUsers.map((x) => x.id as string);

  if (callingstatusCampaigns.length > 0)
    qFilter.callingStatusCampaignIds = callingstatusCampaigns.map((x) => x.id);

  if (commspreferences && commspreferences.length > 0) {
    qFilter.commsPreferencesLabels = commspreferences.map((x) => x.label);
  }

  if (commspreferencesinclusion === true) qFilter.commsPreferencesInclusion = true;
  else if (commspreferencesinclusion === false) qFilter.commsPreferencesInclusion = false;

  return qFilter;
}

